import React, { useState, useEffect, useCallback } from 'react';
import { playPhaseStart, playWarning, playTimeUp, playClick, playMysteryReveal, playVictory } from '../sounds';

const ACCENT = '#FF6B6B';
const ACCENT_LIGHT = 'rgba(255, 107, 107, 0.12)';
const ACCENT_BORDER = 'rgba(255, 107, 107, 0.3)';
const MUTED = '#c4b5d9';
const BG_CARD = 'rgba(255,255,255,0.04)';
const BG_CARD_HOVER = 'rgba(255,255,255,0.08)';

function Timer({ seconds, warning }) {
  return (
    <div className="timer-container">
      <div className={`timer-ring ${warning ? 'warning' : ''}`}>{seconds}</div>
    </div>
  );
}

function Confetti() {
  const pieces = Array.from({ length: 40 }, (_, i) => ({
    left: Math.random() * 100,
    delay: Math.random() * 2,
    color: ['#FFD700', '#DC3545', '#00D4AA', '#6C3CE1', '#FF69B4', '#4CAF50'][i % 6],
    size: 6 + Math.random() * 6,
  }));
  return (
    <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none', zIndex: 999, overflow: 'hidden' }}>
      {pieces.map((p, i) => (
        <div key={i} style={{
          position: 'absolute', top: '-10px', left: `${p.left}%`,
          width: `${p.size}px`, height: `${p.size}px`, background: p.color,
          borderRadius: Math.random() > 0.5 ? '50%' : '2px',
          animation: `confettiFall ${2 + Math.random() * 2}s ${p.delay}s ease-in forwards`,
        }} />
      ))}
      <style>{`
        @keyframes confettiFall {
          0% { transform: translateY(0) rotate(0deg); opacity: 1; }
          100% { transform: translateY(100vh) rotate(720deg); opacity: 0; }
        }
      `}</style>
    </div>
  );
}

function SuspectCard({ suspect, selected, onClick, disabled, compact, expandable }) {
  const [expanded, setExpanded] = useState(false);
  const isClickable = onClick && !disabled;

  const handleClick = () => {
    if (isClickable) {
      onClick(suspect.id);
    } else if (expandable) {
      setExpanded((e) => !e);
    }
  };

  const toggleExpand = (e) => {
    e.stopPropagation();
    setExpanded((prev) => !prev);
  };

  return (
    <button
      onClick={handleClick}
      disabled={disabled && !expandable}
      style={{
        padding: compact ? '10px 12px' : '14px 16px',
        borderRadius: '14px',
        textAlign: 'right',
        cursor: (isClickable || expandable) ? 'pointer' : 'default',
        border: selected
          ? `2px solid ${ACCENT}`
          : '2px solid rgba(255,255,255,0.1)',
        background: selected
          ? ACCENT_LIGHT
          : BG_CARD,
        color: 'white',
        fontFamily: 'Heebo, sans-serif',
        transition: 'all 0.2s',
        width: '100%',
        direction: 'rtl',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: compact ? '0.95rem' : '1.05rem', marginBottom: '2px' }}>
            {suspect.name}
          </div>
          <div style={{ fontSize: '0.8rem', color: ACCENT, marginBottom: (compact && !expanded) ? '0' : '4px' }}>
            {suspect.role}
          </div>
        </div>
        {expandable && suspect.description && (
          <span
            onClick={toggleExpand}
            style={{
              fontSize: '0.7rem', color: MUTED, padding: '6px',
              transition: 'transform 0.2s',
              transform: expanded ? 'rotate(180deg)' : 'rotate(0)',
            }}
          >▼</span>
        )}
      </div>
      {((!compact || expanded) && suspect.description) && (
        <div style={{
          fontSize: '0.8rem', color: MUTED, lineHeight: 1.4,
          marginTop: '4px', paddingTop: expanded ? '6px' : '0',
          borderTop: expanded ? '1px solid rgba(255,255,255,0.08)' : 'none',
        }}>
          {suspect.description}
        </div>
      )}
    </button>
  );
}

function ClueCard({ clue, index }) {
  const borderColor = index < 2 ? ACCENT : '#FF9500';
  return (
    <div style={{
      padding: '12px 16px',
      borderRadius: '12px',
      background: 'rgba(255,255,255,0.04)',
      borderRight: `4px solid ${borderColor}`,
      marginBottom: '8px',
      direction: 'rtl',
    }}>
      <p style={{ fontSize: '0.95rem', lineHeight: 1.5, margin: 0 }}>{clue.text}</p>
    </div>
  );
}

function Mystery({ gameState, playerId, sendMessage, error, clearError }) {
  const {
    phase,
    mysteryTitle,
    premise,
    setting,
    suspects,
    myClues,
    phaseTimeLeft,
    hasAccused,
    accusedCount,
    totalPlayers,
    revealedAccusations,
    solution,
    solutionSuspectId,
    allClueAssignments,
    scores,
    players,
    gameOver,
  } = gameState;

  const [localTimer, setLocalTimer] = useState(phaseTimeLeft || 0);
  const [selectedSuspect, setSelectedSuspect] = useState(null);
  const [confirmed, setConfirmed] = useState(false);
  const [cluesExpanded, setCluesExpanded] = useState(false);
  const [revealIndex, setRevealIndex] = useState(0);
  const isHost = players?.find((p) => p.id === playerId)?.isHost;

  useEffect(() => {
    setLocalTimer(phaseTimeLeft || 0);
  }, [phaseTimeLeft, phase]);

  useEffect(() => {
    if (phase === 'solution' || phase === 'reveal') return;
    const interval = setInterval(() => {
      setLocalTimer((prev) => {
        const next = Math.max(0, prev - 1);
        if (next === 5 || next === 3) playWarning();
        if (next === 0 && prev > 0) playTimeUp();
        return next;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [phase]);

  useEffect(() => {
    if (localTimer === 5 && navigator.vibrate) navigator.vibrate(100);
    if (localTimer === 0 && phase !== 'solution' && phase !== 'reveal' && navigator.vibrate) navigator.vibrate([100, 50, 100]);
  }, [localTimer, phase]);

  useEffect(() => {
    setSelectedSuspect(null);
    setConfirmed(false);
    setCluesExpanded(false);
    setRevealIndex(0);
    // Phase sounds
    if (phase === 'solution') {
      if (gameOver) playVictory();
      else playMysteryReveal();
    } else if (phase === 'reveal') {
      playMysteryReveal();
    } else {
      playPhaseStart();
    }
  }, [phase, gameOver]);

  // Reveal animation: show one accusation at a time
  const revealedEntries = revealedAccusations ? Object.entries(revealedAccusations) : [];
  useEffect(() => {
    if (phase !== 'reveal' || revealedEntries.length === 0) return;
    if (revealIndex >= revealedEntries.length) return;
    const timeout = setTimeout(() => {
      setRevealIndex((prev) => prev + 1);
    }, 1500);
    return () => clearTimeout(timeout);
  }, [phase, revealIndex, revealedEntries.length]);

  const submitAccusation = useCallback(() => {
    if (!selectedSuspect || confirmed) return;
    setConfirmed(true);
    playClick();
    sendMessage('MYSTERY_ACCUSE', { suspectId: selectedSuspect });
  }, [selectedSuspect, confirmed, sendMessage]);

  // ===== INTRO PHASE =====
  if (phase === 'intro') {
    return (
      <div className="card" style={{ direction: 'rtl' }}>
        <Timer seconds={localTimer} warning={localTimer <= 5} />

        <div style={{
          textAlign: 'center', padding: '20px', marginBottom: '20px',
          background: `linear-gradient(135deg, ${ACCENT_LIGHT}, rgba(0,0,0,0.3))`,
          borderRadius: '16px', border: `1px solid ${ACCENT_BORDER}`,
        }}>
          <span style={{ fontSize: '2.5rem', display: 'block', marginBottom: '8px' }}>🔎</span>
          <h1 style={{ fontSize: '1.6rem', color: ACCENT, marginBottom: '8px', fontFamily: 'Heebo, sans-serif' }}>
            {mysteryTitle}
          </h1>
          {setting && (
            <p style={{ fontSize: '0.85rem', color: MUTED, marginBottom: '12px' }}>
              {setting}
            </p>
          )}
        </div>

        {premise && (
          <div style={{
            padding: '16px', borderRadius: '12px', marginBottom: '20px',
            background: BG_CARD, border: '1px solid rgba(255,255,255,0.08)',
            lineHeight: 1.7, fontSize: '0.95rem',
          }}>
            {premise}
          </div>
        )}

        {suspects && suspects.length > 0 && (
          <>
            <h3 style={{ textAlign: 'center', marginBottom: '12px', color: MUTED, fontSize: '0.9rem' }}>
              החשודים
            </h3>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
              gap: '10px',
            }}>
              {suspects.map((s) => (
                <SuspectCard key={s.id} suspect={s} />
              ))}
            </div>
          </>
        )}

        {isHost && (
          <button className="btn btn-primary" onClick={() => sendMessage('MYSTERY_NEXT_PHASE', {})} style={{ marginTop: '16px' }}>
            הבא - הצג ראיות
          </button>
        )}
        {!isHost && <p style={{ textAlign: 'center', color: MUTED, marginTop: '12px', fontSize: '0.85rem' }}>ממתינים שהמנהל ימשיך...</p>}

        {error && <p className="error-msg">{error}</p>}
      </div>
    );
  }

  // ===== CLUES PHASE =====
  if (phase === 'clues') {
    return (
      <div className="card" style={{ direction: 'rtl' }}>
        <Timer seconds={localTimer} warning={localTimer <= 5} />

        <h2 style={{ textAlign: 'center', fontSize: '1.3rem', marginBottom: '16px' }}>
          הראיות שלך 🔍
        </h2>

        {myClues && myClues.length > 0 ? (
          <div style={{ marginBottom: '16px' }}>
            {myClues.map((clue, i) => (
              <ClueCard key={clue.id} clue={clue} index={i} />
            ))}
          </div>
        ) : (
          <p style={{ textAlign: 'center', color: MUTED }}>אין לך ראיות עדיין...</p>
        )}

        <p style={{
          textAlign: 'center', fontSize: '0.85rem', color: MUTED,
          padding: '12px', borderRadius: '12px',
          background: BG_CARD, border: '1px solid rgba(255,255,255,0.08)',
        }}>
          קראו את הראיות בעיון. בקרוב תצטרכו לדון עם שאר השחקנים!
        </p>

        {isHost && (
          <button className="btn btn-primary" onClick={() => sendMessage('MYSTERY_NEXT_PHASE', {})} style={{ marginTop: '16px' }}>
            הבא - התחילו לדון
          </button>
        )}
        {!isHost && <p style={{ textAlign: 'center', color: MUTED, marginTop: '12px', fontSize: '0.85rem' }}>ממתינים שהמנהל ימשיך...</p>}

        {error && <p className="error-msg">{error}</p>}
      </div>
    );
  }

  // ===== DISCUSSION PHASE =====
  if (phase === 'discussion') {
    return (
      <div className="card" style={{ direction: 'rtl' }}>
        <Timer seconds={localTimer} warning={localTimer <= 5} />

        <h2 style={{ textAlign: 'center', fontSize: '1.3rem', marginBottom: '16px' }}>
          דונו ביניכם! 🗣️
        </h2>

        <div style={{
          textAlign: 'center', padding: '14px', marginBottom: '16px',
          background: ACCENT_LIGHT, borderRadius: '12px',
          border: `1px solid ${ACCENT_BORDER}`,
          fontSize: '0.9rem', color: '#FFD700', fontWeight: 700,
        }}>
          שתפו את הרמזים שלכם בעל פה!
        </div>

        {/* Collapsible clues section */}
        <div style={{ marginBottom: '16px' }}>
          <button
            onClick={() => setCluesExpanded(!cluesExpanded)}
            style={{
              width: '100%', padding: '12px 16px', borderRadius: '12px',
              background: BG_CARD_HOVER, border: '1px solid rgba(255,255,255,0.1)',
              color: 'white', cursor: 'pointer', fontFamily: 'Heebo, sans-serif',
              fontSize: '0.9rem', textAlign: 'right', direction: 'rtl',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            }}
          >
            <span>הראיות שלי ({myClues?.length || 0})</span>
            <span style={{ transition: 'transform 0.2s', transform: cluesExpanded ? 'rotate(180deg)' : 'rotate(0)' }}>
              ▼
            </span>
          </button>
          {cluesExpanded && myClues && myClues.length > 0 && (
            <div style={{ marginTop: '8px' }}>
              {myClues.map((clue, i) => (
                <ClueCard key={clue.id} clue={clue} index={i} />
              ))}
            </div>
          )}
        </div>

        {/* Suspect reference list */}
        {suspects && suspects.length > 0 && (
          <div>
            <h4 style={{ color: MUTED, fontSize: '0.85rem', marginBottom: '8px' }}>רשימת חשודים</h4>
            <div style={{ display: 'grid', gap: '6px' }}>
              {suspects.map((s) => (
                <SuspectCard key={s.id} suspect={s} compact expandable />
              ))}
            </div>
          </div>
        )}

        {isHost && (
          <button className="btn btn-primary" onClick={() => sendMessage('MYSTERY_NEXT_PHASE', {})} style={{ marginTop: '16px' }}>
            הבא - הצביעו על האשם
          </button>
        )}
        {!isHost && <p style={{ textAlign: 'center', color: MUTED, marginTop: '12px', fontSize: '0.85rem' }}>ממתינים שהמנהל ימשיך...</p>}

        {error && <p className="error-msg">{error}</p>}
      </div>
    );
  }

  // ===== ACCUSATION PHASE =====
  if (phase === 'accusation') {
    return (
      <div className="card" style={{ direction: 'rtl' }}>
        <Timer seconds={localTimer} warning={localTimer <= 5} />

        <h2 style={{ textAlign: 'center', fontSize: '1.3rem', marginBottom: '16px' }}>
          מי האשם? 🎯
        </h2>

        {!hasAccused && !confirmed ? (
          <>
            <div style={{ display: 'grid', gap: '8px', marginBottom: '16px' }}>
              {suspects && suspects.map((s) => (
                <SuspectCard
                  key={s.id}
                  suspect={s}
                  selected={selectedSuspect === s.id}
                  onClick={(id) => setSelectedSuspect(id)}
                  compact
                  expandable
                />
              ))}
            </div>

            <button
              className="btn btn-primary"
              onClick={submitAccusation}
              disabled={!selectedSuspect}
              style={{
                width: '100%',
                background: selectedSuspect ? ACCENT : undefined,
                borderColor: selectedSuspect ? ACCENT : undefined,
                opacity: selectedSuspect ? 1 : 0.5,
              }}
            >
              בחר! 🎯
            </button>
          </>
        ) : (
          <div style={{
            textAlign: 'center', padding: '24px', marginBottom: '16px',
            background: 'rgba(76, 175, 80, 0.1)', borderRadius: '16px',
            border: '1px solid rgba(76, 175, 80, 0.3)',
          }}>
            <span style={{ fontSize: '2rem', display: 'block', marginBottom: '8px' }}>✓</span>
            <p style={{ fontSize: '1.1rem', fontWeight: 700, color: '#4CAF50' }}>
              הבחירה נשלחה
            </p>
          </div>
        )}

        {accusedCount !== undefined && totalPlayers !== undefined && (
          <p className="waiting" style={{ marginTop: '12px' }}>
            {accusedCount} מתוך {totalPlayers} שחקנים בחרו
          </p>
        )}

        {error && <p className="error-msg">{error}</p>}
      </div>
    );
  }

  // ===== REVEAL PHASE =====
  if (phase === 'reveal') {
    const visibleEntries = revealedEntries.slice(0, revealIndex);
    return (
      <div className="card" style={{ direction: 'rtl' }}>
        <h2 style={{ textAlign: 'center', fontSize: '1.3rem', marginBottom: '20px', color: ACCENT }}>
          חשיפת הבחירות 🔓
        </h2>

        <div style={{ display: 'grid', gap: '10px', marginBottom: '16px' }}>
          {visibleEntries.map(([pid, acc], i) => (
            <div
              key={pid}
              style={{
                padding: '14px 16px', borderRadius: '14px',
                background: BG_CARD,
                border: `1px solid ${ACCENT_BORDER}`,
                animation: `slideUp 0.5s cubic-bezier(0.16, 1, 0.3, 1) both`,
                animationDelay: `${i * 0.1}s`,
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontWeight: 700, fontSize: '1rem' }}>{acc.playerName}</span>
                <span style={{ color: MUTED, fontSize: '0.9rem' }}>→</span>
                <span style={{ fontWeight: 700, fontSize: '1rem', color: ACCENT }}>{acc.suspectName}</span>
              </div>
            </div>
          ))}
        </div>

        {revealIndex < revealedEntries.length && (
          <p className="waiting">חושפים את הבחירות...</p>
        )}

        <style>{`
          @keyframes slideUp {
            from { opacity: 0; transform: translateY(20px); }
            to { opacity: 1; transform: translateY(0); }
          }
        `}</style>

        {error && <p className="error-msg">{error}</p>}
      </div>
    );
  }

  // ===== SOLUTION PHASE =====
  if (phase === 'solution') {
    const culprit = suspects?.find((s) => s.id === solutionSuspectId);
    const sortedPlayers = players ? [...players].sort((a, b) => b.score - a.score) : [];
    const scoreEntries = scores ? Object.entries(scores) : [];

    return (
      <div className="card" style={{ direction: 'rtl' }}>
        {gameOver && <Confetti />}

        <h2 style={{ textAlign: 'center', fontSize: '1.4rem', marginBottom: '20px' }}>
          הפתרון! 🔓
        </h2>

        {/* Culprit reveal */}
        {culprit && (
          <div style={{
            textAlign: 'center', padding: '24px', marginBottom: '20px',
            background: `linear-gradient(135deg, ${ACCENT_LIGHT}, rgba(220,53,69,0.05))`,
            borderRadius: '16px',
            border: `2px solid ${ACCENT}`,
            boxShadow: `0 0 30px ${ACCENT_LIGHT}, 0 0 60px rgba(220,53,69,0.05)`,
            animation: 'bounceIn 0.5s cubic-bezier(0.16, 1, 0.3, 1)',
          }}>
            <span style={{ fontSize: '2.5rem', display: 'block', marginBottom: '8px' }}>🔎</span>
            <h2 style={{ color: ACCENT, fontSize: '1.5rem', marginBottom: '4px' }}>
              {culprit.name}
            </h2>
            <p style={{ fontSize: '0.85rem', color: MUTED }}>{culprit.role}</p>
          </div>
        )}

        {/* Solution explanation */}
        {solution && (
          <div style={{
            padding: '16px', borderRadius: '12px', marginBottom: '20px',
            background: BG_CARD, border: '1px solid rgba(255,255,255,0.08)',
            lineHeight: 1.7, fontSize: '0.95rem',
          }}>
            {solution}
          </div>
        )}

        {/* All clue assignments */}
        {allClueAssignments && Object.keys(allClueAssignments).length > 0 && (
          <div style={{ marginBottom: '20px' }}>
            <h3 style={{ fontSize: '1rem', marginBottom: '12px', color: MUTED }}>
              כל הראיות שחולקו
            </h3>
            {Object.entries(allClueAssignments).map(([playerName, clues]) => (
              <div key={playerName} style={{ marginBottom: '12px' }}>
                <p style={{ fontWeight: 700, fontSize: '0.9rem', marginBottom: '6px', color: ACCENT }}>
                  {playerName}
                </p>
                {clues.map((clueText, i) => (
                  <div key={i} style={{
                    padding: '8px 12px', borderRadius: '8px', marginBottom: '4px',
                    background: BG_CARD, borderRight: `3px solid ${ACCENT_BORDER}`,
                    fontSize: '0.85rem', lineHeight: 1.4,
                  }}>
                    {clueText}
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}

        {/* Score table */}
        {scoreEntries.length > 0 && (
          <div className="result-section" style={{ marginBottom: '20px' }}>
            <h3>ניקוד סיבוב</h3>
            <div style={{ display: 'grid', gap: '6px' }}>
              {scoreEntries.map(([pid, data]) => (
                <div key={pid} style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '10px 14px', borderRadius: '12px',
                  background: data.correct ? 'rgba(76,175,80,0.1)' : BG_CARD,
                  border: data.correct ? '1px solid rgba(76,175,80,0.25)' : '1px solid rgba(255,255,255,0.08)',
                }}>
                  <span style={{ fontWeight: 700 }}>
                    {data.name}
                    {data.correct && <span style={{ color: '#4CAF50', marginRight: '6px' }}> ✓ צדק!</span>}
                  </span>
                  <span style={{
                    fontWeight: 900, fontSize: '1.1rem',
                    color: data.correct ? '#4CAF50' : MUTED,
                  }}>
                    +{data.points}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Overall leaderboard */}
        {sortedPlayers.length > 0 && (
          <div className="result-section" style={{ marginBottom: '20px' }}>
            <h3>טבלת ניקוד</h3>
            <div className="leaderboard">
              {sortedPlayers.map((p, i) => (
                <div key={p.id} className="leaderboard-item">
                  <span className="leaderboard-rank">
                    {i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : `${i + 1}.`}
                  </span>
                  <span className="leaderboard-name">
                    {p.avatar && <span style={{ marginLeft: '4px' }}>{p.avatar}</span>} {p.name}
                    {p.id === playerId && <span className="me-badge"> (אני)</span>}
                  </span>
                  <span className="leaderboard-score">{p.score}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {error && <p className="error-msg">{error}</p>}

        {isHost && !gameOver && (
          <button
            className="btn btn-primary"
            onClick={() => { clearError(); sendMessage('NEXT_ROUND', {}); }}
            style={{ width: '100%', background: ACCENT, borderColor: ACCENT }}
          >
            תעלומה חדשה 🔎
          </button>
        )}

        {isHost && gameOver && (
          <>
            <button
              className="btn btn-primary"
              onClick={() => { clearError(); sendMessage('START_GAME', {}); }}
              style={{ width: '100%', marginBottom: '8px' }}
            >
              שחקו שוב! 🔄
            </button>
            <button
              className="btn btn-secondary"
              onClick={() => { clearError(); sendMessage('NEW_GAME', {}); }}
              style={{ width: '100%', marginBottom: '8px' }}
            >
              חזרה ללובי
            </button>
            <button
              className="btn btn-ghost"
              style={{ width: '100%' }}
              onClick={() => {
                const text = `🔎 תעלומה - תוצאות\n\n${[...(players || [])].sort((a, b) => b.score - a.score).map((p, i) => `${i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : `${i + 1}.`} ${p.name}: ${p.score} נקודות`).join('\n')}\n\nשחקו גם: ${window.location.origin}`;
                navigator.clipboard.writeText(text).catch(() => {});
              }}
            >
              שתף תוצאות
            </button>
          </>
        )}

        {!isHost && !gameOver && (
          <p className="waiting">ממתינים לתעלומה הבאה...</p>
        )}

        {!isHost && gameOver && (
          <p className="waiting">ממתינים שהמנהל יבחר מה לעשות...</p>
        )}

        <style>{`
          @keyframes bounceIn {
            0% { transform: scale(0.8); opacity: 0; }
            100% { transform: scale(1); opacity: 1; }
          }
          @keyframes slideUp {
            from { opacity: 0; transform: translateY(20px); }
            to { opacity: 1; transform: translateY(0); }
          }
        `}</style>
      </div>
    );
  }

  // ===== LOADING FALLBACK =====
  return (
    <div className="card" style={{ direction: 'rtl' }}>
      <p style={{ textAlign: 'center', color: MUTED }}>טוען...</p>
    </div>
  );
}

export default Mystery;
